/**
 * Shared admin auth for all Rehub Studio apps (photoclear, staging, sketchplan).
 * Uses the shared admin_users table; same roles apply everywhere.
 *
 * Visual shell and control classes live in `./ui` (`@rehub/admin/ui`).
 */
import type { SupabaseClient } from '@supabase/supabase-js';

export type AdminRole = 'admin' | 'superadmin' | 'editor';

/** Hub is the account admin: credits, status, and free HDR edits. */
export const HUB_ADMIN_ORIGIN = 'https://hub.rehub.studio';

const HUB_USER_PATH = /^admin\/users\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function hubAdminUserPath(userId: string): string {
  return `admin/users/${userId}`;
}

/**
 * Open a user's account in Hub admin. Passes the current session so Hub
 * does not ask the admin to sign in again, then lands on that user.
 */
export async function openHubAdminUser(
  supabase: SupabaseClient,
  userId: string,
): Promise<void> {
  const next = hubAdminUserPath(userId);
  let href = `${HUB_ADMIN_ORIGIN}/#/${next}`;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token && session?.refresh_token) {
      const params = new URLSearchParams({
        sso_access_token: session.access_token,
        sso_refresh_token: session.refresh_token,
        next,
      });
      href = `${HUB_ADMIN_ORIGIN}/#${params.toString()}`;
    }
  } catch {
    // Open Hub without a session handoff.
  }
  window.location.assign(href);
}

/** Only Hub admin paths may be restored after SSO. */
export function safeHubAdminNext(next: string | null): string | null {
  if (!next) return null;
  if (next === 'admin') return 'admin';
  if (HUB_USER_PATH.test(next)) return next;
  return null;
}

/**
 * Get the admin role for a user from the shared admin_users table.
 * Returns null if not configured, not found, or on error.
 */
export async function getAdminRole(
  supabase: SupabaseClient,
  userId: string
): Promise<AdminRole | null> {
  try {
    const { data, error } = await supabase
      .from('admin_users')
      .select('role')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('Get admin role error:', error);
      return null;
    }

    const role = data?.role;
    if (role === 'admin' || role === 'superadmin' || role === 'editor') {
      return role;
    }
    return null;
  } catch (err) {
    console.error('Failed to get admin role:', err);
    return null;
  }
}

/**
 * Returns true if the user has any admin role (admin, superadmin, or editor).
 * Use this to gate "show admin panel" across all apps.
 */
export async function checkAdminStatus(
  supabase: SupabaseClient,
  userId: string
): Promise<boolean> {
  const role = await getAdminRole(supabase, userId);
  return role === 'admin' || role === 'superadmin' || role === 'editor';
}
